import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { formatError } from "../utils/api";
import Navbar from "../components/Navbar";
import { Check, Zap, Rocket, Crown, Gift, ArrowRight, Plus, Mail, FileText } from "lucide-react";

const TIER_META = {
  trial:        { icon: Gift,   color: "border-sage-200 bg-white",        cta: "Start Free Trial",  badge: null },
  starter:      { icon: Zap,    color: "border-sage-300 bg-sage-50",      cta: "Buy Starter",       badge: null },
  professional: { icon: Rocket, color: "border-amber-300 bg-amber-50",    cta: "Buy Professional",  badge: "Most popular" },
  enterprise:   { icon: Crown,  color: "border-sage-500 bg-[#F3F7F3]",    cta: "Buy Enterprise",    badge: null },
};

export default function PricingPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [publicSettings, setPublicSettings] = useState({ stripe_enabled: true });
  const [requested, setRequested] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    api.get("/my/package").then(r => setData(r.data)).catch(e => setError(formatError(e)));
    api.get("/settings/public").then(r => setPublicSettings(r.data)).catch(() => {});
  }, []);

  const handleBuy = async (item_type, item_key) => {
    setBusy(item_key);
    setError("");
    setInfo("");
    try {
      const res = await api.post("/billing/checkout", { item_type, item_key, origin_url: window.location.origin });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else if (res.data.applied) {
        window.location.href = "/billing/success?applied=1";
      }
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy("");
    }
  };

  const handleRequest = async (item_type, item_key) => {
    setBusy(item_key);
    setError("");
    setInfo("");
    try {
      const res = await api.post("/billing/request-invoice", { item_type, item_key });
      setRequested((r) => ({ ...r, [item_key]: true }));
      setInfo(res.data.message);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy("");
    }
  };

  const stripeOn = publicSettings.stripe_enabled !== false;

  const current = data?.package;
  const tiers = data?.tiers || {};
  const addons = data?.addons || {};

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-center mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Pricing</p>
          <h1 className="font-heading text-3xl font-semibold text-sage-900">Choose your Lean Toolbox plan</h1>
          <p className="text-sage-500 text-sm mt-2 max-w-lg mx-auto">Per-user entitlements. Company slots consume on creation and cannot be reclaimed. Cancel anytime.</p>
          {current && <p className="text-xs text-sage-600 mt-2">Current plan: <span className="font-semibold capitalize">{current.tier}</span> · {current.slots_used}/{current.slots_total} slots · expires {new Date(current.expires_at).toLocaleDateString()}</p>}
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2 mb-4">{error}</div>}
        {info && <div data-testid="pricing-info-banner" className="bg-sage-50 border border-sage-200 text-sage-700 text-sm rounded-md px-3 py-2 mb-4">{info}</div>}
        {!stripeOn && (
          <div data-testid="manual-payment-banner" className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md px-4 py-3 mb-4 flex items-start gap-2">
            <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Online card payment is currently disabled.</p>
              <p className="text-xs mt-0.5">Click <em>Request invoice</em> on any plan and our team will email you a payment link.{publicSettings.billing_contact_email ? <> Or contact <a href={`mailto:${publicSettings.billing_contact_email}`} className="underline">{publicSettings.billing_contact_email}</a>.</> : null}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {Object.entries(tiers).map(([key, t]) => {
            const meta = TIER_META[key] || TIER_META.starter;
            const Icon = meta.icon;
            const isCurrent = current?.tier === key;
            return (
              <div key={key} className={`relative rounded-md border-2 ${meta.color} p-6 flex flex-col`} data-testid={`pricing-card-${key}`}>
                {meta.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">{meta.badge}</span>}
                <Icon className="w-6 h-6 text-sage-600 mb-2" />
                <h3 className="font-heading text-lg font-semibold text-sage-900">{t.name}</h3>
                <p className="font-heading text-3xl font-bold text-sage-900 mt-2">
                  {t.price_usd_monthly === 0 ? "Free" : `$${t.price_usd_monthly}`}
                  {t.price_usd_monthly > 0 && <span className="text-sm text-sage-500 font-normal"> /mo</span>}
                </p>
                <p className="text-xs text-sage-500 mt-2 leading-relaxed">{t.description}</p>
                <ul className="text-sm text-sage-700 mt-4 space-y-2 flex-1">
                  <li className="flex gap-2"><Check className="w-4 h-4 text-sage-500 flex-shrink-0 mt-0.5" /> {t.slots_total} company slot{t.slots_total > 1 ? "s" : ""}</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-sage-500 flex-shrink-0 mt-0.5" /> {t.tools_enabled.length} tool{t.tools_enabled.length > 1 ? "s" : ""} unlocked</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-sage-500 flex-shrink-0 mt-0.5" /> {t.branding ? "Whitelabel PDFs" : "TheKaizenCabinet branding"}</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-sage-500 flex-shrink-0 mt-0.5" /> {t.validity_days}-day period</li>
                </ul>
                <button
                  data-testid={`buy-${key}-button`}
                  onClick={() => (stripeOn || t.price_usd_monthly === 0 ? handleBuy("tier", key) : handleRequest("tier", key))}
                  disabled={busy === key || isCurrent || requested[key]}
                  className="mt-5 w-full bg-sage-600 text-white font-semibold text-sm rounded-md py-2 hover:bg-sage-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isCurrent ? "Current plan"
                    : requested[key] ? <>Invoice requested <Check className="w-3.5 h-3.5" /></>
                    : busy === key ? "..."
                    : (!stripeOn && t.price_usd_monthly > 0)
                      ? <>Request invoice <Mail className="w-3.5 h-3.5" /></>
                      : <>{meta.cta} <ArrowRight className="w-3.5 h-3.5" /></>}
                </button>
              </div>
            );
          })}
        </div>

        <div className="bg-white border border-[#E2E8E3] rounded-md p-6">
          <h3 className="font-heading text-lg font-semibold text-sage-900 mb-1 flex items-center gap-2"><Plus className="w-5 h-5 text-sage-600" /> Add-ons</h3>
          <p className="text-sm text-sage-500 mb-4">Top up existing plan without upgrading tier.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(addons).map(([key, a]) => (
              <div key={key} className="border border-sage-200 rounded-md p-4 flex items-center justify-between" data-testid={`addon-card-${key}`}>
                <div>
                  <p className="font-semibold text-sage-900">{a.name}</p>
                  <p className="text-xs text-sage-500">${a.price_usd.toFixed(2)} one-time</p>
                </div>
                <button
                  data-testid={`buy-addon-${key}-button`}
                  onClick={() => (stripeOn ? handleBuy("addon", key) : handleRequest("addon", key))}
                  disabled={busy === key || !current || requested[key]}
                  className="bg-sage-600 text-white text-xs font-semibold rounded-md px-3 py-2 hover:bg-sage-700 disabled:opacity-50"
                >{busy === key ? "..." : !current ? "Buy a tier first" : requested[key] ? "Requested" : !stripeOn ? "Request invoice" : "Buy"}</button>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
